import { useState } from "react";
import { Send, RotateCcw, Bot, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AGENT_SETTINGS } from "@/constants/testIds";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

/** Simulador de pruebas del agente: chat dentro del panel que usa la
 * configuracion y knowledge base ACTUALES, sin pasar por WhatsApp y sin
 * persistir ninguna conversacion real. */
export default function PlaygroundTab() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    const history = messages.map((m) => ({ direction: m.direction, body: m.body }));
    const userMsg = { id: `u-${Date.now()}`, direction: "inbound", body: text };
    setMessages((prev) => [...prev, userMsg]);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post("/agent/playground", { message: text, history });
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, direction: "outbound", body: data.reply, escalate: data.escalate, llmError: data.llm_error }]);
    } catch (err) {
      toast({ title: "Error al probar el agente", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => setMessages([]);
  const lastEscalated = messages.length > 0 && messages[messages.length - 1].direction === "outbound" && messages[messages.length - 1].escalate;
  // Fix AJ (2026-10-23): si la IA fallo (clave, saldo, modelo), se muestra el motivo claro.
  const lastLlmError = messages.length > 0 ? messages[messages.length - 1].llmError : null;

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-muted-foreground mb-3">
        Escribi como si fueras un lead. El agente responde con el tono, idiomas y knowledge base configurados actualmente - nada de esto se guarda como una conversacion real.
      </p>
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="h-96 overflow-y-auto p-4 space-y-3" data-testid={AGENT_SETTINGS.playgroundMessages}>
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2">
              <Bot className="w-8 h-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Probalo escribiendo un mensaje, por ejemplo "Cuanto cuesta el botox?"</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-end" : "justify-start"}`} data-testid={`${AGENT_SETTINGS.playgroundBubblePrefix}${m.id}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.direction === "inbound" ? "bg-brand text-white" : "bg-muted text-foreground"}`}>
                {m.body}
              </div>
            </div>
          ))}
          {lastLlmError && (
            <div className="flex items-start gap-1.5 text-xs text-destructive justify-center text-center" data-testid={AGENT_SETTINGS.playgroundLlmError}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>La IA no pudo responder: {lastLlmError}</span>
            </div>
          )}
          {lastEscalated && (
            <div className="flex items-center gap-1.5 text-xs text-[#B5793A] justify-center" data-testid={AGENT_SETTINGS.playgroundEscalatedNotice}>
              <AlertTriangle className="w-3.5 h-3.5" /> El agente escalaria esta conversacion a un humano aca.
            </div>
          )}
        </div>
        <div className="p-3 border-t border-border flex items-end gap-2">
          <Textarea
            value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Escribi un mensaje de prueba..."
            className="min-h-[44px] max-h-32 resize-none" data-testid={AGENT_SETTINGS.playgroundInput}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          />
          <Button variant="outline" size="icon" onClick={handleReset} title="Reiniciar prueba" data-testid={AGENT_SETTINGS.playgroundResetButton}>
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button onClick={handleSend} disabled={sending || !draft.trim()} className="bg-brand hover:bg-brand-hover text-white shrink-0" data-testid={AGENT_SETTINGS.playgroundSendButton}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
